/** Service Desk console: merging one organisation into another (ORG-005, SD-S4-07). */
export const serviceMerge = {
  title: "Merge {{name}}",
  step: {
    target: "Step 1 of 2: choose the organisation that stays.",
    review: "Step 2 of 2: check what moves, settle the duplicates and confirm.",
    done: "The merge is done.",
  },
  actions: {
    open: "Merge into…",
    preview: "Preview merge",
    previewAgain: "Preview again",
    apply: "Merge organisations",
    back: "Back",
  },
  target: {
    intro: "Everything that belongs to {{name}} (people, tickets, assets and the rest) moves to the organisation you choose. Nothing is deleted.",
    search: "Find the organisation to keep",
    searchHint: "Search by name or code. Archived organisations are not offered.",
    placeholder: "Search organisations",
    loading: "Looking for organisations",
    errorTitle: "Organisations couldn't load",
    errorBody: "Check your connection and try again. Nothing was changed.",
    emptyTitle: "No organisation to merge into",
    emptyBody: "Add another organisation first, then come back to merge.",
    emptySearch: "No active organisation matches “{{term}}”. Try a shorter name or its code.",
    results_one: "{{count}} organisation",
    results_other: "{{count}} organisations",
    contacts_one: "{{count}} contact",
    contacts_other: "{{count}} contacts",
    note: "You will see exactly what moves and what clashes before anything happens.",
  },
  review: {
    source: "Goes away (archived)",
    target: "Stays",
    summary: "{{source}} is archived with a note that points to {{target}}. Its records move to {{target}}, and {{target}}'s own details win where both have one.",
  },
  counts: {
    title: "What moves",
    preview: "Preview",
    caption: "Records moving from {{source}} to {{target}}",
    type: "Record type",
    count: "Records",
    total: "Total",
    nothing: "This organisation holds no records to move. Only its details are folded in.",
    discarded_one: "{{count}} cached connector entry is discarded and rebuilt on the next sync.",
    discarded_other: "{{count}} cached connector entries are discarded and rebuilt on the next sync.",
  },
  groups: {
    contacts: "Contacts",
    tickets: "Tickets",
    assets: "Assets",
    contracts: "Contracts",
    projects: "Projects",
    visits: "Visits",
    orders: "Orders",
    quotes: "Quotes",
    carts: "Carts",
    approvals: "Approvals",
    rmas: "RMAs",
    sites: "Sites",
    attachments: "Attachments",
    catalogue: "Catalogue visibility",
    leads: "Lead conversions",
    other: "Other records",
  },
  fields: {
    title: "Organisation details",
    filled: "Taken from the organisation that goes away:",
    kept: "Kept from the organisation that stays (both have one):",
    domainsMoved: "E-mail domains carried over:",
    domainsLeft: "E-mail domains left out (over the limit):",
    notesTrimmed: "Notes:",
    notesTrimmedBody: "joined, and the oldest lines were trimmed to fit.",
    industry: "industry",
    tier: "tier",
    account_manager_user_id: "account manager",
    default_team_id: "default team",
    price_list_ref: "price list",
    payment_terms: "payment terms",
    billing_address: "billing address",
    shipping_address: "shipping address",
    order_settings: "ordering rules",
    portal_settings: "portal settings",
    attributes: "custom attributes",
    tin: "TIN",
  },
  conflicts: {
    title_one: "{{count}} duplicate to settle",
    title_other: "{{count}} duplicates to settle",
    intro: "Both organisations hold the same thing. Choose how each one is handled; the merge stays locked until every choice is made.",
    none: "No duplicates. Nothing needs your choice.",
    list: "Duplicates to settle",
    blockedList: "Duplicates that block the merge",
    blockedDefault: "This cannot be settled here. Fix it in the record, then preview again.",
    choose: "Choose one",
    chosen: "Chosen",
    kind: {
      site_name: "Both have a site named “{{name}}”.",
      catalogue_visibility: "Both organisations limit which products they can see.",
      contact_email: "A contact with the e-mail {{email}} exists in both organisations.",
      asset_serial: "An asset with serial {{serial}} ({{manufacturer}}) exists in both organisations.",
    },
    option: {
      merge: "Fold them into one site",
      rename: "Keep both sites",
      keep_target: "Keep the rules of the organisation that stays",
      use_source: "Use the rules of the organisation that goes away",
    },
    optionHint: {
      merge: "Everything that points at the old site moves to the one that stays, and the old site is archived.",
      rename: "The old site is renamed with the organisation's name in brackets so the two can be told apart.",
      keep_target: "The product rules of the organisation that goes away are dropped.",
      use_source: "The product rules of the organisation that stays are replaced by the other's.",
    },
    blocked: {
      contact_email: "A person can only be listed once. Archive or move one of the two contacts, then preview again.",
      asset_serial: "A serial number can only be recorded once. Correct or archive one of the two assets, then preview again.",
    },
  },
  confirm: {
    title: "This cannot be undone",
    body: "{{source}} will be archived and everything above moves for good. To confirm, type the phrase below.",
    label: "Type the phrase to confirm",
    hint: "Type exactly:",
    ready: "Ready to merge.",
    why: {
      blocked: "A duplicate above blocks the merge. Fix it, then preview again.",
      unsettled: "Choose how each duplicate is handled to unlock the merge.",
      phrase: "Type the phrase exactly to unlock the merge.",
    },
  },
  progress: {
    title_one: "Moving {{count}} record to {{target}}…",
    title_other: "Moving {{count}} records to {{target}}…",
    hint: "This runs as one step. If anything fails, nothing is changed and you can try again.",
  },
  result: {
    done_one: "{{source}} was merged into {{target}}. {{count}} record moved.",
    done_other: "{{source}} was merged into {{target}}. {{count}} records moved.",
    archived: "{{source}} is archived and keeps a pointer to where it went.",
    openTarget: "Open {{target}}",
    moved: "Records moved by table ({{count}})",
    auditTitle: "Audit trail",
    auditRecorded: "The merge was recorded in the audit log.",
    auditAction: "Organisation merged",
    auditSystem: "System",
  },
  pointer: {
    prefix: "Merged into",
    on: " on {{date}}. Its people, tickets and assets now belong there.",
  },
  errors: {
    stale: "The organisations changed since you previewed. Nothing was merged. Preview again to see the current state.",
    notConfirmed: "Confirm the merge to continue. It cannot be undone.",
    same: "An organisation cannot be merged into itself.",
    notFound: "One of the organisations is no longer there. Close this and reload.",
    forbidden: "You don't have permission to merge organisations.",
  },
} as const;
